import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Save, ChefHat, IndianRupee, Loader2, ArrowLeft, CheckCircle, Upload, FileText, Image, Camera, Gift, Users, Copy } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MENU_CATEGORIES = [
  "Starters", "Main Course", "Rice & Biryani", "Breads", "Chinese",
  "South Indian", "Fast Food", "Rolls & Wraps", "Desserts", "Beverages",
  "Snacks", "Combos", "Specials", "Thali", "Salads"
];

export const AddMenu = () => {
  const navigate = useNavigate();
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  
  // Restaurant Documents
  const [restaurantPhoto, setRestaurantPhoto] = useState(null);
  const [restaurantPhotoPreview, setRestaurantPhotoPreview] = useState('');
  const [fssaiCertificate, setFssaiCertificate] = useState(null);
  const [fssaiCertificatePreview, setFssaiCertificatePreview] = useState('');
  const [menuPdf, setMenuPdf] = useState(null);
  
  const [menuItems, setMenuItems] = useState([
    {
      id: 1,
      name: '',
      price: '',
      category: '',
      is_veg: true,
    }
  ]);

  useEffect(() => {
    const storedPartner = localStorage.getItem('fudco_restaurant_partner');
    if (!storedPartner) {
      toast.error('Please complete restaurant registration first');
      navigate('/restaurant-partner');
      return;
    }
    const partnerData = JSON.parse(storedPartner);
    setPartner(partnerData);
    // Generate referral code
    setReferralCode(`FUDCO${partnerData.phone?.slice(-4) || 'REST'}${Math.random().toString(36).substring(2, 6).toUpperCase()}`);
  }, [navigate]);

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    if (type === 'restaurantPhoto') {
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file');
        return;
      }
      setRestaurantPhoto(file);
      setRestaurantPhotoPreview(URL.createObjectURL(file));
    } else if (type === 'fssaiCertificate') {
      if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
        toast.error('Please upload an image or PDF file');
        return;
      }
      setFssaiCertificate(file);
      if (file.type.startsWith('image/')) {
        setFssaiCertificatePreview(URL.createObjectURL(file));
      } else {
        setFssaiCertificatePreview('pdf');
      }
    } else if (type === 'menuPdf') {
      if (file.type !== 'application/pdf') {
        toast.error('Please upload a PDF file');
        return;
      }
      setMenuPdf(file);
    }
  };

  const addMenuItem = () => {
    setMenuItems([
      ...menuItems,
      {
        id: Date.now(),
        name: '',
        price: '',
        category: '',
        is_veg: true,
      }
    ]);
  };

  const copyReferralCode = () => {
    navigator.clipboard.writeText(referralCode);
    toast.success('Referral code copied!');
  };

  const removeMenuItem = (id) => {
    if (menuItems.length === 1) {
      toast.error('You need at least one menu item');
      return;
    }
    setMenuItems(menuItems.filter(item => item.id !== id));
  };

  const updateMenuItem = (id, field, value) => {
    setMenuItems(menuItems.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const handleSubmit = async () => {
    // Validate required documents
    if (!restaurantPhoto) {
      toast.error('Restaurant photo is required');
      return;
    }
    
    if (!fssaiCertificate) {
      toast.error('FSSAI Certificate is required');
      return;
    }

    // Validate menu items
    const validItems = menuItems.filter(item => 
      item.name.trim() && item.price && item.category
    );

    if (validItems.length === 0 && !menuPdf) {
      toast.error('Please add at least one menu item or upload a menu PDF');
      return;
    }

    setLoading(true);

    try {
      // In production, you would upload files to cloud storage
      // For demo, we'll just save the metadata
      await axios.post(`${API}/partners/restaurant/menu`, {
        partner_id: partner.id,
        restaurant_photo: restaurantPhoto?.name || '',
        fssai_certificate: fssaiCertificate?.name || '',
        menu_pdf: menuPdf?.name || '',
        items: validItems.map(item => ({
          name: item.name,
          price: parseFloat(item.price),
          category: item.category,
          is_veg: item.is_veg,
        }))
      });

      toast.success('Menu and documents uploaded successfully!');
      
      // Clear partner info
      localStorage.removeItem('fudco_restaurant_partner');
      
      // Show success modal
      setShowSuccessModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    if (!restaurantPhoto || !fssaiCertificate) {
      toast.error('Restaurant photo and FSSAI Certificate are required');
      return;
    }
    toast.success('Registration complete!');
    localStorage.removeItem('fudco_restaurant_partner');
    setShowSuccessModal(true);
  };

  if (!partner) {
    return null;
  }

  return (
    <div className="min-h-screen pt-20 bg-gray-50" data-testid="add-menu-page">
      {/* Header */}
      <section className="bg-white border-b border-gray-200 py-6">
        <div className="max-w-6xl mx-auto px-4">
          <button
            onClick={() => navigate('/restaurant-partner')}
            className="flex items-center gap-2 text-gray-600 hover:text-[#2D8B2E] mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <div className="flex items-center gap-4">
            <div className="bg-[#2D8B2E] text-white p-3 rounded-2xl">
              <ChefHat className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Restaurant Setup
              </h1>
              <p className="text-gray-600">{partner.name}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Progress Indicator */}
      <div className="bg-white border-b border-gray-200 py-4">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#2D8B2E] text-white flex items-center justify-center">
                <CheckCircle className="w-5 h-5" />
              </div>
              <span className="text-sm font-medium text-[#2D8B2E]">Registration</span>
            </div>
            <div className="flex-1 h-1 bg-[#2D8B2E] rounded"></div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#2D8B2E] text-white flex items-center justify-center">
                <CheckCircle className="w-5 h-5" />
              </div>
              <span className="text-sm font-medium text-[#2D8B2E]">Verification</span>
            </div>
            <div className="flex-1 h-1 bg-[#2D8B2E] rounded"></div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#2D8B2E] text-white flex items-center justify-center font-semibold">
                3
              </div>
              <span className="text-sm font-medium text-[#2D8B2E]">Documents & Menu</span>
            </div>
          </div>
        </div>
      </div>

      {/* Documents Section */}
      <section className="py-8">
        <div className="max-w-6xl mx-auto px-4">
          <Card className="p-6 mb-6" data-testid="documents-section">
            <h2 className="text-xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Required Documents
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              {/* Restaurant Photo */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Camera className="w-4 h-4 text-[#2D8B2E]" />
                  Restaurant Photo *
                </Label>
                <div 
                  className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all hover:border-[#2D8B2E] ${
                    restaurantPhoto ? 'border-[#2D8B2E] bg-green-50' : 'border-gray-300'
                  }`}
                  onClick={() => document.getElementById('restaurantPhoto').click()}
                >
                  {restaurantPhotoPreview ? (
                    <div className="space-y-2">
                      <img 
                        src={restaurantPhotoPreview} 
                        alt="Restaurant" 
                        className="w-full h-32 object-cover rounded-lg mx-auto"
                      />
                      <p className="text-sm text-[#2D8B2E] font-medium">{restaurantPhoto?.name}</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Image className="w-10 h-10 text-gray-400 mx-auto" />
                      <p className="text-sm text-gray-500">Click to upload restaurant photo</p>
                      <p className="text-xs text-gray-400">JPG, PNG (max 5MB)</p>
                    </div>
                  )}
                </div>
                <input
                  type="file"
                  id="restaurantPhoto"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFileChange(e, 'restaurantPhoto')}
                  data-testid="restaurant-photo-input"
                />
              </div>

              {/* FSSAI Certificate */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#2D8B2E]" />
                  FSSAI Certificate *
                </Label>
                <div 
                  className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all hover:border-[#2D8B2E] ${
                    fssaiCertificate ? 'border-[#2D8B2E] bg-green-50' : 'border-gray-300'
                  }`}
                  onClick={() => document.getElementById('fssaiCertificate').click()}
                >
                  {fssaiCertificatePreview ? (
                    <div className="space-y-2">
                      {fssaiCertificatePreview === 'pdf' ? (
                        <FileText className="w-16 h-16 text-red-500 mx-auto" />
                      ) : (
                        <img 
                          src={fssaiCertificatePreview} 
                          alt="FSSAI" 
                          className="w-full h-32 object-cover rounded-lg mx-auto"
                        />
                      )}
                      <p className="text-sm text-[#2D8B2E] font-medium">{fssaiCertificate?.name}</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Upload className="w-10 h-10 text-gray-400 mx-auto" />
                      <p className="text-sm text-gray-500">Click to upload FSSAI certificate</p>
                      <p className="text-xs text-gray-400">JPG, PNG, PDF (max 5MB)</p>
                    </div>
                  )}
                </div>
                <input
                  type="file"
                  id="fssaiCertificate"
                  accept="image/*,.pdf"
                  className="hidden"
                  onChange={(e) => handleFileChange(e, 'fssaiCertificate')}
                  data-testid="fssai-certificate-input"
                />
              </div>

              {/* Menu PDF */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#2D8B2E]" />
                  Menu PDF (Optional)
                </Label>
                <div 
                  className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all hover:border-[#2D8B2E] ${
                    menuPdf ? 'border-[#2D8B2E] bg-green-50' : 'border-gray-300'
                  }`}
                  onClick={() => document.getElementById('menuPdf').click()}
                >
                  {menuPdf ? (
                    <div className="space-y-2">
                      <FileText className="w-16 h-16 text-red-500 mx-auto" />
                      <p className="text-sm text-[#2D8B2E] font-medium">{menuPdf?.name}</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <FileText className="w-10 h-10 text-gray-400 mx-auto" />
                      <p className="text-sm text-gray-500">Upload complete menu as PDF</p>
                      <p className="text-xs text-gray-400">PDF only (max 10MB)</p>
                    </div>
                  )}
                </div>
                <input
                  type="file"
                  id="menuPdf"
                  accept=".pdf"
                  className="hidden"
                  onChange={(e) => handleFileChange(e, 'menuPdf')}
                  data-testid="menu-pdf-input"
                />
              </div>
            </div>
          </Card>

          {/* Menu Items Section */}
          <Card className="p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Add Menu Items
            </h2>
            <p className="text-gray-500 text-sm mb-6">Add individual items or skip if you've uploaded a menu PDF</p>

            <div className="space-y-4">
              {menuItems.map((item, index) => (
                <div key={item.id} className="p-4 bg-gray-50 rounded-xl" data-testid={`menu-item-${index}`}>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-semibold text-gray-900">Item {index + 1}</h3>
                    {menuItems.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeMenuItem(item.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        data-testid={`remove-item-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>

                  <div className="grid md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label>Item Name *</Label>
                      <Input
                        value={item.name}
                        onChange={(e) => updateMenuItem(item.id, 'name', e.target.value)}
                        placeholder="e.g., Paneer Butter Masala"
                        className="h-10 rounded-lg"
                        data-testid={`item-name-${index}`}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Price (₹) *</Label>
                      <div className="relative">
                        <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          type="number"
                          value={item.price}
                          onChange={(e) => updateMenuItem(item.id, 'price', e.target.value)}
                          placeholder="199"
                          className="h-10 pl-10 rounded-lg"
                          data-testid={`item-price-${index}`}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Category *</Label>
                      <Select 
                        value={item.category} 
                        onValueChange={(value) => updateMenuItem(item.id, 'category', value)}
                      >
                        <SelectTrigger className="h-10 rounded-lg" data-testid={`item-category-${index}`}>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          {MENU_CATEGORIES.map((cat) => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Type</Label>
                      <div className="flex items-center gap-3 h-10 px-3 bg-white rounded-lg border">
                        <div className={`w-4 h-4 rounded-full ${item.is_veg ? 'bg-green-500' : 'bg-red-500'}`} />
                        <span className="text-sm flex-1">{item.is_veg ? 'Veg' : 'Non-Veg'}</span>
                        <Switch
                          checked={item.is_veg}
                          onCheckedChange={(checked) => updateMenuItem(item.id, 'is_veg', checked)}
                          data-testid={`item-veg-${index}`}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Add More Button */}
              <Button
                variant="outline"
                onClick={addMenuItem}
                className="w-full h-12 rounded-xl border-dashed border-2 border-[#2D8B2E] text-[#2D8B2E] hover:bg-green-50"
                data-testid="add-item-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add Another Item
              </Button>
            </div>
          </Card>

          {/* Refer & Earn Section */}
          <Card className="p-6 mb-6 bg-gradient-to-br from-green-50 to-white border-green-200" data-testid="refer-earn-section">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-[#2D8B2E] text-white p-3 rounded-2xl">
                <Gift className="w-6 h-6" />
              </div>
              <h2 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Refer a Friend & Earn
              </h2>
            </div>
            
            <p className="text-gray-600 mb-4">
              Share your referral code and earn rewards when your friends join Fudco!
            </p>

            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="bg-white p-4 rounded-xl border border-green-200">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-5 h-5 text-[#2D8B2E]" />
                  <span className="font-semibold">Refer Restaurant</span>
                </div>
                <p className="text-2xl font-bold text-[#2D8B2E]">₹50 <span className="text-sm font-normal text-gray-500">per referral</span></p>
              </div>
              <div className="bg-white p-4 rounded-xl border border-yellow-200">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-5 h-5 text-[#FFD700]" />
                  <span className="font-semibold">Refer Delivery Partner</span>
                </div>
                <p className="text-2xl font-bold text-[#2D8B2E]">₹50 <span className="text-sm font-normal text-gray-500">per referral</span></p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex-1 bg-gray-100 px-4 py-3 rounded-xl font-mono text-lg font-bold text-[#2D8B2E]">
                {referralCode}
              </div>
              <Button 
                onClick={copyReferralCode}
                className="bg-[#2D8B2E] hover:bg-[#236c24] text-white px-6"
                data-testid="copy-referral-btn"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy
              </Button>
            </div>
          </Card>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              variant="outline"
              onClick={handleSkip}
              className="flex-1 h-14 rounded-full text-gray-600"
              data-testid="skip-btn"
            >
              Skip Menu Items
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 h-14 bg-[#2D8B2E] hover:bg-[#236c24] text-white rounded-full font-semibold"
              data-testid="save-menu-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  Save & Complete
                </>
              )}
            </Button>
          </div>
        </div>
      </section>

      {/* Success Modal */}
      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent className="sm:max-w-md" data-testid="success-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Thank You!
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 mt-4 text-center">
            <div className="bg-green-100 text-[#2D8B2E] p-6 rounded-full inline-block">
              <CheckCircle className="w-12 h-12" />
            </div>
            <p className="text-gray-700 text-lg font-medium">
              We will reach out to you soon when we go Live with our Application!
            </p>
            <p className="text-gray-500 text-sm">
              Our team will contact you on your registered phone number.
            </p>
            <Button
              onClick={() => {
                setShowSuccessModal(false);
                navigate('/');
              }}
              className="w-full h-12 bg-[#2D8B2E] hover:bg-[#236c24] text-white rounded-full font-semibold"
              data-testid="success-close-btn"
            >
              Back to Home
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
